import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Mic, Volume2, CheckCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { Button, Text } from "@core/index";
import podcastBanner from "@packages/assets/banners/podcast.png";
import { useGetMyAssignments } from "@packages/api/madlenAPI";
import type { GetMyAssignmentsParams } from "@packages/api/madlenAPI.schemas";

const PronunciationAssessmentView = () => {
  const { t, i18n } = useTranslation();
  const [page] = useState<number>(1);
  const [size] = useState<number>(30);

  const params = useMemo<GetMyAssignmentsParams>(
    () => ({ page, size }),
    [page, size],
  );
  const { data, isLoading, isError } = useGetMyAssignments(params, {
    query: {},
  });
  const assessments = data?.assignments ?? [];
  const bannerImageUrl = podcastBanner;

  const formatRelativeTime = (input?: string | number | Date) => {
    if (!input) return "";
    const date = new Date(input);
    if (isNaN(date.getTime())) return "";
    const diffSeconds = Math.round((date.getTime() - Date.now()) / 1000);
    const rtf = new Intl.RelativeTimeFormat(i18n.language, { numeric: "auto" });
    const units: [Intl.RelativeTimeFormatUnit, number][] = [
      ["year", 60 * 60 * 24 * 365],
      ["month", 60 * 60 * 24 * 30],
      ["week", 60 * 60 * 24 * 7],
      ["day", 60 * 60 * 24],
      ["hour", 60 * 60],
      ["minute", 60],
      ["second", 1],
    ];
    for (const [unit, sec] of units) {
      if (Math.abs(diffSeconds) >= sec || unit === "second") {
        const value = Math.round(diffSeconds / sec);
        return rtf.format(value, unit);
      }
    }
    return "";
  };

  return (
    <div className="flex-1 md:p-8 p-4 lg:mt-5 bg-gray-50 dark:bg-dark-500 lg:rounded-tl-3xl overflow-y-auto h-full custom-scrollbar">
      <div className="max-w-7xl mx-auto">
        {/* Header with title and create button */}
        <div className="mb-4 sm:mb-6 lg:mb-8 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 sm:gap-4">
          <div className="min-w-0 flex-1">
            <Text
              variant="h1"
              className="text-lg sm:text-xl lg:text-2xl xl:text-3xl break-words"
            >
              {t("pronunciationAssessment.title")}
            </Text>
            <Text
              variant="p"
              className="text-gray-600 dark:text-gray-400 mt-1 text-sm sm:text-base break-words"
            >
              {t("pronunciationAssessment.description")}
            </Text>
          </div>
          <div className="flex-shrink-0 w-full sm:w-auto">
            <Link to="/content/pronunciation-assessment/create">
              <Button
                size="md"
                rounded="full"
                className="bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white w-full sm:w-auto"
              >
                <span className="flex items-center gap-2 justify-center">
                  <Plus className="w-4 h-4" />
                  {t("pronunciationAssessment.createButton")}
                </span>
              </Button>
            </Link>
          </div>
        </div>

        {/* Banner under header: left image, right text with emphasized CTA word */}
        <div className="mb-4 sm:mb-6 lg:mb-8">
          <div className="relative overflow-hidden rounded-2xl border border-dark-200 dark:border-dark-400 bg-white dark:bg-dark-600">
            {/* Gradient background overlay */}
            <div
              className="absolute inset-0 pointer-events-none z-0"
              style={{
                background:
                  "linear-gradient(135deg, rgba(0, 102, 255, 0.10) 0%, rgba(0, 0, 0, 0.85) 100%)",
              }}
            />
            <div className="flex flex-col md:flex-row h-full p-4 items-center relative z-10">
              {/* Left image */}
              <div className="md:w-2/6 w-full h-28 relative flex items-center justify-start">
                <img
                  src={bannerImageUrl}
                  alt=""
                  className="w-full h-full object-contain"
                  style={{ position: "static" }}
                />
              </div>
              {/* Right text */}
              <div className="flex-1 p-5 sm:p-6 lg:p-8 flex items-end justify-end">
                <div>
                  <Text
                    variant="p"
                    className="text-gray-700 dark:text-gray-200 text-sm sm:text-base lg:text-lg leading-relaxed"
                  >
                    Give your students <b>pronunciation</b> assignments
                    <br />
                    or grade their pronunciation.
                  </Text>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main content card */}
        <div className="mt-3 sm:mt-4 lg:mt-6">
          {isLoading ? (
            <div className="text-center py-16">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-cyan-500 mx-auto mb-4"></div>
              <Text variant="p" className="text-gray-600 dark:text-gray-400">
                {t("common.loading")}...
              </Text>
            </div>
          ) : isError ? (
            <div className="text-center py-16">
              <Text variant="p" className="text-red-500">
                {t("common.error")}
              </Text>
            </div>
          ) : assessments.length === 0 ? (
            <div className="text-center py-16">
              <div className="mx-auto w-24 h-24 bg-cyan-100 dark:bg-cyan-900 rounded-full flex items-center justify-center mb-6">
                <Mic className="w-12 h-12 text-cyan-600 dark:text-cyan-400" />
              </div>
              <Text variant="h2" className="mb-2">
                {t("pronunciationAssessment.empty.title")}
              </Text>
              <Text
                variant="p"
                status="secondary"
                className="mb-8 max-w-md mx-auto"
              >
                {t("pronunciationAssessment.empty.description")}
              </Text>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {assessments.map((assessment) => (
                <div
                  key={assessment.id}
                  className="bg-white dark:bg-dark-600 rounded-xl shadow-md p-6 border border-gray-200 dark:border-gray-700"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-cyan-100 dark:bg-cyan-900 rounded-full flex items-center justify-center">
                        <Volume2 className="w-5 h-5 text-cyan-600 dark:text-cyan-400" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 dark:text-white">
                          {assessment.title &&
                          String(assessment.title).trim().length > 0
                            ? assessment.title
                            : t(
                                "pronunciationAssessment.create.basicInfo.titlePlaceholder",
                              )}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {formatRelativeTime(assessment.createdAt)}
                        </p>
                      </div>
                    </div>
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  </div>

                  {/* Assessment details: accent, language, CEFR level */}
                  <div className="mb-4 space-y-2">
                    {/* Content Language and Accent */}
                    <div className="flex items-center gap-2 text-xs">
                      <span className="text-gray-500 dark:text-gray-400">
                        {t("interactive_material.create.contentLanguage")}:
                      </span>
                      <span className="font-medium text-gray-700 dark:text-gray-300">
                        {assessment.contentLanguage === "en" &&
                          t("interactive_material.create.contentLanguages.en")}
                        {assessment.contentLanguage === "tr" &&
                          t("interactive_material.create.contentLanguages.tr")}
                        {assessment.contentLanguage === "de" &&
                          t("interactive_material.create.contentLanguages.de")}
                        {assessment.contentLanguage === "fr" &&
                          t("interactive_material.create.contentLanguages.fr")}
                        {assessment.contentLanguage === "it" &&
                          t("interactive_material.create.contentLanguages.it")}
                        {assessment.contentLanguage === "es" &&
                          t("interactive_material.create.contentLanguages.es")}
                      </span>
                      {assessment.accent &&
                        assessment.contentLanguage === "en" && (
                          <>
                            <span className="text-gray-400">•</span>
                            <span className="text-gray-500 dark:text-gray-400">
                              {t("pronunciationAssessment.create.accent.title")}
                              :
                            </span>
                            <span className="font-medium text-gray-700 dark:text-gray-300">
                              {assessment.accent === "BRITISH" &&
                                t(
                                  "pronunciationAssessment.create.accent.british",
                                )}
                              {assessment.accent === "AMERICAN" &&
                                t(
                                  "pronunciationAssessment.create.accent.american",
                                )}
                            </span>
                          </>
                        )}
                    </div>

                    {/* CEFR Level */}
                    {assessment.cefrLevel && (
                      <div className="flex items-center gap-2 text-xs">
                        <span className="text-gray-500 dark:text-gray-400">
                          {t("pronunciationAssessment.create.cefrLevel.title")}:
                        </span>
                        <span className="font-medium text-gray-700 dark:text-gray-300">
                          {assessment.cefrLevel === "PRE_A1" && "Pre-A1"}
                          {assessment.cefrLevel === "A1" && "A1"}
                          {assessment.cefrLevel === "A2" && "A2"}
                          {assessment.cefrLevel === "B1" && "B1"}
                          {assessment.cefrLevel === "B2" && "B2"}
                          {assessment.cefrLevel === "C1" && "C1"}
                          {assessment.cefrLevel === "C2" && "C2"}
                        </span>
                      </div>
                    )}

                    {/* Generation Type */}
                    {assessment.generationType && (
                      <div className="flex items-center gap-2 text-xs">
                        <span className="text-gray-500 dark:text-gray-400">
                          {t(
                            "pronunciationAssessment.create.assessmentScope.title",
                          )}
                          :
                        </span>
                        <span className="font-medium text-gray-700 dark:text-gray-300">
                          {assessment.generationType === "WORD" &&
                            t(
                              "pronunciationAssessment.create.assessmentScope.wordType",
                            )}
                          {assessment.generationType === "SENTENCE" &&
                            t(
                              "pronunciationAssessment.create.assessmentScope.sentenceType",
                            )}
                        </span>
                      </div>
                    )}
                  </div>

                  {false && (
                    <p className="text-gray-600 dark:text-gray-300 text-sm mb-4">
                      {/* No description field in summary dto */}
                    </p>
                  )}
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {assessment.itemsCount || 0}{" "}
                      {assessment.generationType === "WORD"
                        ? t(
                            "pronunciationAssessment.create.assessmentScope.wordType",
                          ).toLowerCase()
                        : t(
                            "pronunciationAssessment.create.assessmentScope.sentenceType",
                          ).toLowerCase()}
                    </span>
                    <Link
                      to={`/content/pronunciation-assessment/${assessment.id}`}
                    >
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-cyan-200 text-cyan-600 hover:bg-cyan-50 dark:border-cyan-700 dark:text-cyan-400 dark:hover:bg-cyan-900/20"
                      >
                        {t("common.view")}
                      </Button>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PronunciationAssessmentView;
